/**
 * SiteAlarms — route child at `/project/:id/alarms`, nested under the
 * ProjectSummary layout that renders the PersistentKPIBar above this page.
 *
 * Single-column, block-grouped list (djin-cloud#625). Replaces the previous
 * master-detail tree + detail panel, which gated its entire structure on the
 * config.site_devices roster call and silently flattened to an unattributed
 * list whenever that call failed (#624).
 *
 * Layout mirrors the portfolio hub (CustomerAlarms) so the two alarm surfaces
 * read the same, with one deliberate divergence each way:
 *   - grouping nests one level deeper here (device sub-group within a block)
 *   - cards hold a stable roster order instead of sorting severity-desc
 *
 * Data flow:
 *   - useSiteAlarms — server-side site scoping + status/severity pre-filter.
 *     Client-side matchesFilter is the display source of truth.
 *   - useSiteDevices — the quiet strip ONLY. Never structural. Its failure is
 *     surfaced in the strip (#624), not swallowed.
 *   - useAlarmSubscription — live WS transitions; do not rename the
 *     ['alarms', siteId] query-key prefix or invalidation stops firing.
 *
 * Pane-of-glass: no mutation UI. All state derives from edge transitions.
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlarmFilterCluster,
  AlarmRollupBanner,
  DEFAULT_FILTER_VALUE,
  alarmBucket,
  deriveSeverityCounts,
  mapEquipmentChip,
  type FilterValue,
} from '../components/alarms/primitives';
import { AlarmSlideOver } from '../components/alarms/AlarmSlideOver';
import { BlockCard } from '../components/alarms/site/BlockCard';
import { QuietDeviceStrip } from '../components/alarms/site/QuietDeviceStrip';
import { groupAlarmsByBlockAndDevice } from '../components/alarms/site/alarm-grouping';
import {
  decodeAlarmIdentity,
  encodeAlarmIdentity,
} from '../components/alarms/alarm-deep-link';
import { SiteDetailTabs } from '../components/site-detail/SiteDetailTabs';
import { useProjectSummarySite } from '../hooks/useProjectSummarySite';
import { useSiteAlarms } from '../hooks/useSiteAlarms';
import { useSiteDevices } from '../hooks/useSiteDevices';
import { useAlarmSubscription } from '../hooks/useAlarmSubscription';
import { useSites } from '../hooks/useSites';
import type { ActiveAlarm } from '../types/alarm.types';

/** Windows matching the FilterValue.timeRange chips. */
const TIME_RANGE_SECONDS: Record<FilterValue['timeRange'], number> = {
  '1h': 3600,
  '4h': 4 * 3600,
  '24h': 24 * 3600,
  '7d': 7 * 86400,
  '30d': 30 * 86400,
};

export function SiteAlarms() {
  const { id: siteId = '' } = useParams<{ id: string }>();
  const site = useProjectSummarySite();

  useAlarmSubscription(siteId);

  const sitesQ = useSites();
  const siteDisplayName =
    sitesQ.data?.sites.find((s) => s.site_id === siteId)?.name ?? '—';

  const [filter, setFilter] = useState<FilterValue>(DEFAULT_FILTER_VALUE);
  const [searchParams, setSearchParams] = useSearchParams();

  // URL owns slide-over state, so alarms deep-link and survive refresh.
  const selectedIdentity = useMemo(
    () => decodeAlarmIdentity(searchParams.get('alarm')),
    [searchParams],
  );

  const openAlarm = useCallback(
    (a: ActiveAlarm) => {
      const next = new URLSearchParams(searchParams);
      // a.site_id is the wire site_code, which is what AlarmSlideOver resolves
      // against config.sites.site_code. Never substitute the route :id UUID.
      next.set(
        'alarm',
        encodeAlarmIdentity({
          siteId: a.site_id,
          deviceId: a.device_id,
          conditionKey: a.condition_key,
        }),
      );
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const closeSlideOver = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('alarm');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  // Time-window chips are meaningful for History only. The site list endpoint
  // has no raised_after parameter (unlike the customer endpoint), so the
  // window is applied client-side in matchesFilter.
  //
  // Deliberately "history ALONE", not "history among others": in a blended
  // selection the cutoff would also apply to the active rows and hide
  // long-standing alarms an operator must not miss. Blended History is instead
  // bounded by the API's own 30-day window.
  const isHistoryOnly = filter.status.length === 1 && filter.status[0] === 'history';

  // The full status array goes to the hook, which decides which SOURCE(S) to
  // read. Collapsing it to a scalar here is what made "History + Needs
  // Attention" silently return no history at all (#628) — the two buckets live
  // in different tables and a single scalar cannot ask for both.
  const alarmsQ = useSiteAlarms(siteId, {
    statuses: filter.status,
    severity: filter.severity.length === 1 ? filter.severity[0] : undefined,
  });
  const devicesQ = useSiteDevices(siteId);

  // Recomputed per filter change, not per tick — matches the portfolio page's
  // tradeoff (the window only needs to be right at interaction time).
  const historyCutoffMs = useMemo(
    () => (isHistoryOnly ? Date.now() - TIME_RANGE_SECONDS[filter.timeRange] * 1000 : null),
    [isHistoryOnly, filter.timeRange],
  );

  const filtered = useMemo<ActiveAlarm[]>(() => {
    const all = alarmsQ.data?.alarms ?? [];
    return all.filter((a) => matchesFilter(a, filter, historyCutoffMs));
  }, [alarmsQ.data, filter, historyCutoffMs]);

  const counts = useMemo(() => deriveSeverityCounts(filtered), [filtered]);
  const groups = useMemo(() => groupAlarmsByBlockAndDevice(filtered), [filtered]);
  const alarmingDeviceIds = useMemo(
    () => new Set(filtered.map((a) => a.device_id)),
    [filtered],
  );

  return (
    <div
      className="flex flex-col flex-1 min-h-0 bg-ems-bg text-ems-text-primary overflow-y-auto"
      data-testid="site-alarms-page"
    >
      <SiteDetailTabs siteId={siteId} site={site} />

      <div className="flex flex-col gap-4 p-4">
        {alarmsQ.isError && (
          <div className="flex items-center justify-between gap-3 text-xs text-ems-red border border-ems-red/30 rounded p-3">
            <span>Could not load alarms.</span>
            <button
              type="button"
              onClick={() => alarmsQ.refetch()}
              className="px-3 py-1 rounded border border-ems-border text-ems-text-secondary hover:text-ems-text-primary"
            >
              Retry
            </button>
          </div>
        )}

        <AlarmFilterCluster
          value={filter}
          onChange={setFilter}
          variant="expanded"
          showTimeFilter={isHistoryOnly}
        />

        <AlarmRollupBanner scope="site" name={siteDisplayName} counts={counts} />

        <section className="flex flex-col gap-3" data-testid="active-alarms-section">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ems-text-secondary">
            Active alarms ({filtered.length})
          </h2>
          {alarmsQ.isLoading ? (
            <p className="text-xs text-ems-text-muted py-4 text-center">Loading…</p>
          ) : groups.length === 0 ? (
            <div className="text-xs text-ems-text-muted border border-ems-border rounded p-3 flex items-center justify-between gap-3">
              <span>No alarms match the current filter.</span>
              <button
                type="button"
                onClick={() => setFilter(DEFAULT_FILTER_VALUE)}
                className="text-ems-blue hover:underline focus:outline-none focus:ring-2 focus:ring-ems-blue/40 rounded"
              >
                Clear filters
              </button>
            </div>
          ) : (
            groups.map((g) => <BlockCard key={g.key} group={g} onAlarmClick={openAlarm} />)
          )}
        </section>

        <QuietDeviceStrip
          devices={devicesQ.data ?? []}
          alarmingDeviceIds={alarmingDeviceIds}
          isError={devicesQ.isError}
          onRetry={() => devicesQ.refetch()}
        />
      </div>

      <AlarmSlideOver identity={selectedIdentity} onClose={closeSlideOver} />
    </div>
  );
}

function matchesFilter(
  a: ActiveAlarm,
  f: FilterValue,
  historyCutoffMs: number | null,
): boolean {
  if (f.severity.length > 0 && !f.severity.includes(a.severity)) return false;
  if (f.status.length > 0 && !f.status.includes(alarmBucket(a))) return false;
  if (f.equipment.length > 0) {
    const chip = mapEquipmentChip(a.device_type);
    if (!chip || !f.equipment.includes(chip)) return false;
  }
  if (historyCutoffMs !== null && new Date(a.raised_at).getTime() < historyCutoffMs) {
    return false;
  }
  if (f.search) {
    const q = f.search.toLowerCase();
    const hay = `${a.condition_label} ${a.device_id} ${a.condition_key}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}
